import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  INVENTORY_MOVEMENT_TYPES,
  ITEM_CONDITIONS,
  MAINTENANCE_TYPES,
  type InventoryMovementType,
  type ItemCondition,
  type MaintenanceType,
} from '@tabita/contracts';
import { formatDate, formatDateTime } from '@tabita/i18n';
import { useAuth } from '@/auth/auth-context';
import { useI18n } from '@/i18n/i18n-provider';
import { inventoryApi } from '@/services/inventory.api';
import { peopleApi } from '@/services/people.api';
import { ApiError } from '@/services/api-client';
import { Alert, Button, EmptyState, Field, Spinner } from '@/components/ui';

/**
 * One item: its details, its stock ledger, who has it checked out, and its
 * maintenance history — the same "one page, several sections" shape
 * `GroupDetailPage` uses for a roster, rather than a separate route per
 * sub-resource.
 */
export function ItemDetailPage() {
  const { id = '' } = useParams();
  const { t, locale } = useI18n();
  const { can } = useAuth();
  const queryClient = useQueryClient();

  const [notice, setNotice] = useState<string | null>(null);
  const [isRecordingMovement, setIsRecordingMovement] = useState(false);
  const [isCheckingOut, setIsCheckingOut] = useState(false);
  const [isSchedulingMaintenance, setIsSchedulingMaintenance] = useState(false);

  const query = useQuery({
    queryKey: ['inventory', 'items', id],
    queryFn: ({ signal }) => inventoryApi.getItem(id, signal),
  });

  const movements = useQuery({
    queryKey: ['inventory', 'movements', id],
    queryFn: ({ signal }) => inventoryApi.listMovements({ itemId: id, limit: 20 }, signal),
    enabled: can('inventory.movement.read'),
  });

  const checkouts = useQuery({
    queryKey: ['inventory', 'checkouts', id],
    queryFn: ({ signal }) => inventoryApi.listCheckouts({ itemId: id, limit: 20 }, signal),
    enabled: can('inventory.checkout.read'),
  });

  const maintenance = useQuery({
    queryKey: ['inventory', 'maintenance', id],
    queryFn: ({ signal }) => inventoryApi.listMaintenance({ itemId: id, limit: 20 }, signal),
    enabled: can('inventory.maintenance.read'),
  });

  const refreshAll = () => {
    void query.refetch();
    void movements.refetch();
    void checkouts.refetch();
  };

  const archive = useMutation({
    mutationFn: (reason: string) => inventoryApi.archiveItem(id, reason),
    onSuccess: (item) => {
      queryClient.setQueryData(['inventory', 'items', id], item);
      setNotice(t('inventory.item.archived', { values: { name: item.name } }));
    },
  });

  const restore = useMutation({
    mutationFn: () => inventoryApi.restoreItem(id),
    onSuccess: (item) => {
      queryClient.setQueryData(['inventory', 'items', id], item);
      setNotice(t('inventory.item.restored', { values: { name: item.name } }));
    },
  });

  const returnCheckout = useMutation({
    mutationFn: (input: { checkoutId: string; returnCondition: ItemCondition }) =>
      inventoryApi.returnCheckout(input.checkoutId, { returnCondition: input.returnCondition }),
    onSuccess: () => {
      setNotice(t('inventory.checkout.returned'));
      refreshAll();
    },
  });

  const reportLost = useMutation({
    mutationFn: (checkoutId: string) => inventoryApi.reportLostCheckout(checkoutId, {}),
    onSuccess: () => {
      setNotice(t('inventory.checkout.reportedLost'));
      refreshAll();
    },
  });

  if (query.isPending) return <Spinner label={t('common.states.loading')} />;

  if (query.isError) {
    return (
      <div className="page">
        <Link to="/inventory" className="back-link">
          {t('common.actions.back')}
        </Link>
        <Alert tone="danger">{describeError(query.error, t)}</Alert>
      </div>
    );
  }

  const item = query.data;
  const isArchived = item.archivedAt !== null;

  return (
    <div className="page">
      <Link to="/inventory" className="back-link">
        {t('common.actions.back')}
      </Link>

      <header className="page-header">
        <div>
          <h1 className="page-title">{item.name}</h1>
          <p className="page-count" dir="ltr">
            {item.quantityOnHand} {item.unit}
            {item.isLowStock && (
              <span className="cell-flag"> · {t('inventory.item.lowStock')}</span>
            )}
          </p>
        </div>

        <div className="page-actions">
          {can('inventory.item.archive') &&
            (isArchived ? (
              <Button
                variant="secondary"
                loading={restore.isPending}
                onClick={() => restore.mutate()}
              >
                {t('common.actions.restore')}
              </Button>
            ) : (
              <ArchiveButton name={item.name} onConfirm={(reason) => archive.mutate(reason)} />
            ))}
        </div>
      </header>

      {notice && <Alert tone="success">{notice}</Alert>}
      {isArchived && <Alert tone="warning">{t('inventory.item.archivedHint')}</Alert>}

      <dl className="detail-grid">
        <Detail label={t('inventory.category.singular')} value={item.categoryName} />
        <Detail label={t('inventory.location.singular')} value={item.locationName} />
        <Detail label={t('inventory.item.type')} value={t(`inventory.type.${item.type}`)} />
        <Detail
          label={t('inventory.item.condition')}
          value={t(`inventory.condition.${item.condition}`)}
        />
        <Detail label={t('inventory.item.sku')} value={item.sku} dir="ltr" />
        <Detail label={t('inventory.item.serialNumber')} value={item.serialNumber} dir="ltr" />
        <Detail label={t('inventory.supplier.singular')} value={item.supplierName} />
        <Detail
          label={t('inventory.item.minQuantity')}
          value={item.minQuantity === null ? null : `${item.minQuantity} ${item.unit}`}
          dir="ltr"
        />
        <Detail
          label={t('inventory.item.acquisitionDate')}
          value={item.acquisitionDate ? formatDate(item.acquisitionDate, locale) : null}
        />
        <Detail label={t('inventory.item.notes')} value={item.notes} />
      </dl>

      {/* --- Movements ------------------------------------------------------- */}
      <section className="settings-card">
        <header className="page-header">
          <h2 className="page-title">{t('inventory.movement.title')}</h2>
          {can('inventory.movement.create') && !isArchived && (
            <Button variant="secondary" onClick={() => setIsRecordingMovement(true)}>
              {t('inventory.movement.record')}
            </Button>
          )}
        </header>

        {!movements.data || movements.data.data.length === 0 ? (
          <EmptyState title={t('common.states.empty')} />
        ) : (
          <div className="table-scroll">
            <table className="table">
              <thead>
                <tr>
                  <th scope="col">{t('inventory.movement.occurredAt')}</th>
                  <th scope="col">{t('inventory.movement.type')}</th>
                  <th scope="col">{t('inventory.movement.quantity')}</th>
                  <th scope="col">{t('inventory.movement.reason')}</th>
                </tr>
              </thead>
              <tbody>
                {movements.data.data.map((movement) => (
                  <tr key={movement.id}>
                    <td>{formatDateTime(movement.occurredAt, locale)}</td>
                    <td>{t(`inventory.movementType.${movement.type}`)}</td>
                    <td dir="ltr">
                      {movement.quantity > 0 ? '+' : ''}
                      {movement.quantity} {item.unit}
                      {movement.type === 'TRANSFER' &&
                        movement.fromLocationName &&
                        movement.toLocationName && (
                          <span className="cell-empty">
                            {' '}
                            ({movement.fromLocationName} → {movement.toLocationName})
                          </span>
                        )}
                    </td>
                    <td>{movement.reason ?? <span className="cell-empty">—</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* --- Checkouts --------------------------------------------------------- */}
      <section className="settings-card">
        <header className="page-header">
          <h2 className="page-title">{t('inventory.checkout.title')}</h2>
          {can('inventory.checkout.manage') && !isArchived && (
            <Button variant="secondary" onClick={() => setIsCheckingOut(true)}>
              {t('inventory.checkout.create')}
            </Button>
          )}
        </header>

        {!checkouts.data || checkouts.data.data.length === 0 ? (
          <EmptyState title={t('common.states.empty')} />
        ) : (
          <div className="table-scroll">
            <table className="table">
              <thead>
                <tr>
                  <th scope="col">{t('people.person.singular')}</th>
                  <th scope="col">{t('inventory.checkout.checkedOutAt')}</th>
                  <th scope="col">{t('inventory.checkout.dueAt')}</th>
                  <th scope="col">{t('common.labels.status')}</th>
                  <th scope="col" />
                </tr>
              </thead>
              <tbody>
                {checkouts.data.data.map((checkout) => (
                  <tr key={checkout.id} data-archived={checkout.isOverdue ? true : undefined}>
                    <td>
                      <Link to={`/people/${checkout.borrowerPersonId}`} className="person-name">
                        {checkout.borrowerDisplayName}
                      </Link>
                    </td>
                    <td>{formatDate(checkout.checkedOutAt, locale)}</td>
                    <td>
                      {checkout.dueAt ? (
                        formatDate(checkout.dueAt, locale)
                      ) : (
                        <span className="cell-empty">—</span>
                      )}
                      {checkout.isOverdue && (
                        <span className="cell-flag"> · {t('inventory.checkout.overdue')}</span>
                      )}
                    </td>
                    <td>{t(`inventory.checkout.status.${checkout.status}`)}</td>
                    <td>
                      {checkout.status === 'OUT' && can('inventory.checkout.manage') && (
                        <ReturnActions
                          onReturn={(returnCondition) =>
                            returnCheckout.mutate({ checkoutId: checkout.id, returnCondition })
                          }
                          onReportLost={() => reportLost.mutate(checkout.id)}
                        />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* --- Maintenance --------------------------------------------------------- */}
      <section className="settings-card">
        <header className="page-header">
          <h2 className="page-title">{t('inventory.maintenance.title')}</h2>
          {can('inventory.maintenance.create') && !isArchived && (
            <Button variant="secondary" onClick={() => setIsSchedulingMaintenance(true)}>
              {t('inventory.maintenance.create')}
            </Button>
          )}
        </header>

        {!maintenance.data || maintenance.data.data.length === 0 ? (
          <EmptyState title={t('common.states.empty')} />
        ) : (
          <div className="table-scroll">
            <table className="table">
              <thead>
                <tr>
                  <th scope="col">{t('inventory.maintenance.type')}</th>
                  <th scope="col">{t('inventory.maintenance.scheduledDate')}</th>
                  <th scope="col">{t('inventory.maintenance.completedDate')}</th>
                  <th scope="col">{t('inventory.maintenance.nextDueDate')}</th>
                </tr>
              </thead>
              <tbody>
                {maintenance.data.data.map((record) => (
                  <tr key={record.id}>
                    <td>{t(`inventory.maintenanceType.${record.type}`)}</td>
                    <td>
                      {record.scheduledDate ? (
                        formatDate(record.scheduledDate, locale)
                      ) : (
                        <span className="cell-empty">—</span>
                      )}
                    </td>
                    <td>
                      {record.completedDate ? (
                        formatDate(record.completedDate, locale)
                      ) : (
                        <span className="cell-empty">—</span>
                      )}
                    </td>
                    <td>
                      {record.nextDueDate ? (
                        formatDate(record.nextDueDate, locale)
                      ) : (
                        <span className="cell-empty">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {isRecordingMovement && (
        <MovementDialog
          itemId={id}
          unit={item.unit}
          onClose={() => setIsRecordingMovement(false)}
          onRecorded={() => {
            setIsRecordingMovement(false);
            refreshAll();
          }}
        />
      )}

      {isCheckingOut && (
        <CheckOutDialog
          itemId={id}
          onClose={() => setIsCheckingOut(false)}
          onCheckedOut={() => {
            setIsCheckingOut(false);
            refreshAll();
          }}
        />
      )}

      {isSchedulingMaintenance && (
        <MaintenanceDialog
          itemId={id}
          onClose={() => setIsSchedulingMaintenance(false)}
          onCreated={() => {
            setIsSchedulingMaintenance(false);
            void maintenance.refetch();
          }}
        />
      )}
    </div>
  );
}

// --- Pieces ------------------------------------------------------------------

function Detail({ label, value, dir }: { label: string; value: string | null; dir?: 'ltr' }) {
  return (
    <div className="detail-row">
      <dt className="detail-label">{label}</dt>
      <dd className="detail-value" dir={dir}>
        {value ?? <span className="cell-empty">—</span>}
      </dd>
    </div>
  );
}

function ArchiveButton({ name, onConfirm }: { name: string; onConfirm: (reason: string) => void }) {
  const { t } = useI18n();
  const [isOpen, setIsOpen] = useState(false);
  const [reason, setReason] = useState('');

  if (!isOpen) {
    return (
      <Button variant="danger" onClick={() => setIsOpen(true)}>
        {t('common.actions.archive')}
      </Button>
    );
  }

  return (
    <form
      className="inline-confirm"
      onSubmit={(event: FormEvent) => {
        event.preventDefault();
        onConfirm(reason.trim());
      }}
    >
      <p>{t('inventory.item.archiveConfirm', { values: { name } })}</p>
      <Field
        label={t('inventory.item.archiveReason')}
        name="reason"
        value={reason}
        onChange={(event) => setReason(event.target.value)}
        required
        autoFocus
      />
      <Button type="button" variant="ghost" onClick={() => setIsOpen(false)}>
        {t('common.actions.cancel')}
      </Button>
      <Button type="submit" variant="danger" disabled={reason.trim().length === 0}>
        {t('common.actions.confirm')}
      </Button>
    </form>
  );
}

function ReturnActions({
  onReturn,
  onReportLost,
}: {
  onReturn: (returnCondition: ItemCondition) => void;
  onReportLost: () => void;
}) {
  const { t } = useI18n();
  const [condition, setCondition] = useState<ItemCondition>('GOOD');

  return (
    <div className="inline-confirm">
      <select
        aria-label={t('inventory.checkout.returnCondition')}
        className="field-input"
        value={condition}
        onChange={(event) => setCondition(event.target.value as ItemCondition)}
      >
        {ITEM_CONDITIONS.map((value) => (
          <option key={value} value={value}>
            {t(`inventory.condition.${value}`)}
          </option>
        ))}
      </select>
      <Button variant="secondary" onClick={() => onReturn(condition)}>
        {t('inventory.checkout.return')}
      </Button>
      <Button variant="ghost" onClick={onReportLost}>
        {t('inventory.checkout.reportLost')}
      </Button>
    </div>
  );
}

function MovementDialog({
  itemId,
  unit,
  onClose,
  onRecorded,
}: {
  itemId: string;
  unit: string;
  onClose: () => void;
  onRecorded: () => void;
}) {
  const { t } = useI18n();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [form, setForm] = useState({
    type: 'RECEIPT' as InventoryMovementType,
    quantity: '',
    fromLocationId: '',
    toLocationId: '',
    reason: '',
  });

  const locations = useQuery({
    queryKey: ['inventory', 'locations', 'options'],
    queryFn: ({ signal }) => inventoryApi.listLocations({ limit: 100 }, signal),
    enabled: form.type === 'TRANSFER',
  });

  useEffect(() => {
    dialogRef.current?.showModal();
  }, []);

  const mutation = useMutation({
    mutationFn: () =>
      inventoryApi.recordMovement(itemId, {
        type: form.type,
        quantity: Number(form.quantity),
        fromLocationId: form.type === 'TRANSFER' ? form.fromLocationId : null,
        toLocationId: form.type === 'TRANSFER' ? form.toLocationId : null,
        reason: form.reason.trim() || null,
      }),
    onSuccess: onRecorded,
    onError: (error: unknown) => {
      setFormError(
        error instanceof ApiError
          ? t(`errors.${error.code}`, { fallback: t('errors.INTERNAL_ERROR') })
          : t('errors.INTERNAL_ERROR'),
      );
    },
  });

  return (
    <dialog
      ref={dialogRef}
      onClose={onClose}
      className="dialog"
      aria-labelledby="movement-form-title"
    >
      <form
        className="dialog-body"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          setFormError(null);
          mutation.mutate();
        }}
      >
        <h2 id="movement-form-title">{t('inventory.movement.record')}</h2>

        {formError && <Alert tone="danger">{formError}</Alert>}

        <div className="field">
          <label className="field-label" htmlFor="field-movement-type">
            {t('inventory.movement.type')}
          </label>
          <select
            id="field-movement-type"
            className="field-input"
            value={form.type}
            onChange={(e) => setForm({ ...form, type: e.target.value as InventoryMovementType })}
          >
            {INVENTORY_MOVEMENT_TYPES.map((type) => (
              <option key={type} value={type}>
                {t(`inventory.movementType.${type}`)}
              </option>
            ))}
          </select>
        </div>

        <Field
          label={`${t('inventory.movement.quantity')} (${unit})`}
          name="quantity"
          type="number"
          value={form.quantity}
          onChange={(e) => setForm({ ...form, quantity: e.target.value })}
          required
          dir="ltr"
        />

        {form.type === 'TRANSFER' && (
          <div className="form-grid">
            <div className="field">
              <label className="field-label" htmlFor="field-fromLocationId">
                {t('inventory.movement.fromLocation')}
              </label>
              <select
                id="field-fromLocationId"
                className="field-input"
                value={form.fromLocationId}
                onChange={(e) => setForm({ ...form, fromLocationId: e.target.value })}
                required
              >
                <option value="">—</option>
                {(locations.data?.data ?? []).map((location) => (
                  <option key={location.id} value={location.id}>
                    {location.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="field">
              <label className="field-label" htmlFor="field-toLocationId">
                {t('inventory.movement.toLocation')}
              </label>
              <select
                id="field-toLocationId"
                className="field-input"
                value={form.toLocationId}
                onChange={(e) => setForm({ ...form, toLocationId: e.target.value })}
                required
              >
                <option value="">—</option>
                {(locations.data?.data ?? []).map((location) => (
                  <option key={location.id} value={location.id}>
                    {location.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}

        <Field
          label={t('inventory.movement.reason')}
          name="reason"
          value={form.reason}
          onChange={(e) => setForm({ ...form, reason: e.target.value })}
        />

        <footer className="dialog-actions">
          <Button type="button" variant="ghost" onClick={onClose}>
            {t('common.actions.cancel')}
          </Button>
          <Button type="submit" variant="primary" loading={mutation.isPending}>
            {t('common.actions.save')}
          </Button>
        </footer>
      </form>
    </dialog>
  );
}

function CheckOutDialog({
  itemId,
  onClose,
  onCheckedOut,
}: {
  itemId: string;
  onClose: () => void;
  onCheckedOut: () => void;
}) {
  const { t } = useI18n();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [search, setSearch] = useState('');
  const [borrowerPersonId, setBorrowerPersonId] = useState<string | null>(null);
  const [borrowerName, setBorrowerName] = useState('');
  const [quantity, setQuantity] = useState('1');
  const [dueAt, setDueAt] = useState('');
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    dialogRef.current?.showModal();
  }, []);

  const people = useQuery({
    queryKey: ['people', 'picker', search],
    queryFn: ({ signal }) => peopleApi.list({ search, limit: 8 }, signal),
    enabled: !borrowerPersonId && search.trim().length >= 2,
  });

  const mutation = useMutation({
    mutationFn: () =>
      inventoryApi.checkOut(itemId, {
        borrowerPersonId: borrowerPersonId!,
        quantity: Number(quantity),
        dueAt: dueAt ? new Date(dueAt).toISOString() : null,
      }),
    onSuccess: onCheckedOut,
    onError: (error: unknown) => {
      setFormError(
        error instanceof ApiError
          ? t(`errors.${error.code}`, { fallback: t('errors.INTERNAL_ERROR') })
          : t('errors.INTERNAL_ERROR'),
      );
    },
  });

  return (
    <dialog
      ref={dialogRef}
      onClose={onClose}
      className="dialog"
      aria-labelledby="checkout-form-title"
    >
      <form
        className="dialog-body"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          setFormError(null);
          mutation.mutate();
        }}
      >
        <h2 id="checkout-form-title">{t('inventory.checkout.create')}</h2>

        {formError && <Alert tone="danger">{formError}</Alert>}

        {borrowerPersonId ? (
          <p className="field-hint">
            {t('inventory.checkout.borrower')}: <strong>{borrowerName}</strong>{' '}
            <Button
              variant="ghost"
              type="button"
              onClick={() => {
                setBorrowerPersonId(null);
                setBorrowerName('');
              }}
            >
              {t('common.actions.cancel')}
            </Button>
          </p>
        ) : (
          <div className="member-picker">
            <Field
              label={t('inventory.checkout.borrower')}
              name="borrower-search"
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder={t('groups.group.choosePerson')}
              required
              autoFocus
            />
            {(people.data?.data ?? []).length > 0 && (
              <ul className="picker-results">
                {(people.data?.data ?? []).map((person) => (
                  <li key={person.id}>
                    <Button
                      variant="ghost"
                      type="button"
                      onClick={() => {
                        setBorrowerPersonId(person.id);
                        setBorrowerName(person.displayName);
                        setSearch('');
                      }}
                    >
                      {person.displayName}
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        <div className="form-grid">
          <Field
            label={t('inventory.checkout.quantity')}
            name="quantity"
            type="number"
            min={1}
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
            dir="ltr"
          />
          <Field
            label={t('inventory.checkout.dueAt')}
            name="dueAt"
            type="date"
            value={dueAt}
            onChange={(e) => setDueAt(e.target.value)}
            dir="ltr"
          />
        </div>

        <footer className="dialog-actions">
          <Button type="button" variant="ghost" onClick={onClose}>
            {t('common.actions.cancel')}
          </Button>
          <Button
            type="submit"
            variant="primary"
            loading={mutation.isPending}
            disabled={!borrowerPersonId}
          >
            {t('common.actions.save')}
          </Button>
        </footer>
      </form>
    </dialog>
  );
}

function MaintenanceDialog({
  itemId,
  onClose,
  onCreated,
}: {
  itemId: string;
  onClose: () => void;
  onCreated: () => void;
}) {
  const { t } = useI18n();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [form, setForm] = useState({
    type: 'SCHEDULED' as MaintenanceType,
    scheduledDate: '',
    cost: '',
    performedBy: '',
    notes: '',
    nextDueDate: '',
  });

  useEffect(() => {
    dialogRef.current?.showModal();
  }, []);

  const mutation = useMutation({
    mutationFn: () =>
      inventoryApi.createMaintenance(itemId, {
        type: form.type,
        scheduledDate: form.scheduledDate || null,
        cost: form.cost ? Number(form.cost) : null,
        performedBy: form.performedBy.trim() || null,
        notes: form.notes.trim() || null,
        nextDueDate: form.nextDueDate || null,
      }),
    onSuccess: onCreated,
    onError: (error: unknown) => {
      setFormError(
        error instanceof ApiError
          ? t(`errors.${error.code}`, { fallback: t('errors.INTERNAL_ERROR') })
          : t('errors.INTERNAL_ERROR'),
      );
    },
  });

  return (
    <dialog
      ref={dialogRef}
      onClose={onClose}
      className="dialog"
      aria-labelledby="maintenance-form-title"
    >
      <form
        className="dialog-body"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          setFormError(null);
          mutation.mutate();
        }}
      >
        <h2 id="maintenance-form-title">{t('inventory.maintenance.create')}</h2>

        {formError && <Alert tone="danger">{formError}</Alert>}

        <div className="field">
          <label className="field-label" htmlFor="field-maintenance-type">
            {t('inventory.maintenance.type')}
          </label>
          <select
            id="field-maintenance-type"
            className="field-input"
            value={form.type}
            onChange={(e) => setForm({ ...form, type: e.target.value as MaintenanceType })}
          >
            {MAINTENANCE_TYPES.map((type) => (
              <option key={type} value={type}>
                {t(`inventory.maintenanceType.${type}`)}
              </option>
            ))}
          </select>
        </div>

        <div className="form-grid">
          <Field
            label={t('inventory.maintenance.scheduledDate')}
            name="scheduledDate"
            type="date"
            value={form.scheduledDate}
            onChange={(e) => setForm({ ...form, scheduledDate: e.target.value })}
            dir="ltr"
          />
          <Field
            label={t('inventory.maintenance.nextDueDate')}
            name="nextDueDate"
            type="date"
            value={form.nextDueDate}
            onChange={(e) => setForm({ ...form, nextDueDate: e.target.value })}
            dir="ltr"
          />
        </div>

        <div className="form-grid">
          <Field
            label={t('inventory.maintenance.cost')}
            name="cost"
            type="number"
            min={0}
            value={form.cost}
            onChange={(e) => setForm({ ...form, cost: e.target.value })}
            dir="ltr"
          />
          <Field
            label={t('inventory.maintenance.performedBy')}
            name="performedBy"
            value={form.performedBy}
            onChange={(e) => setForm({ ...form, performedBy: e.target.value })}
          />
        </div>

        <Field
          label={t('inventory.maintenance.notes')}
          name="notes"
          value={form.notes}
          onChange={(e) => setForm({ ...form, notes: e.target.value })}
        />

        <footer className="dialog-actions">
          <Button type="button" variant="ghost" onClick={onClose}>
            {t('common.actions.cancel')}
          </Button>
          <Button type="submit" variant="primary" loading={mutation.isPending}>
            {t('common.actions.save')}
          </Button>
        </footer>
      </form>
    </dialog>
  );
}

function describeError(error: unknown, t: ReturnType<typeof useI18n>['t']): string {
  return error instanceof ApiError
    ? t(`errors.${error.code}`, { fallback: t('errors.INTERNAL_ERROR') })
    : t('errors.INTERNAL_ERROR');
}
